/**
 * The all-regions roster (BON-534).
 *
 * A CSS grid rather than a `<table>`, because a child row has to sit under its
 * parent on the same alignment without nesting a second table. The ARIA table
 * roles are carried explicitly so the semantics survive that choice.
 *
 * The header cells, the body cells and the grid track list all derive from the
 * one filtered column array, so a hidden column cannot leave a track with no
 * cell behind it.
 */
import { nestRows } from '../../primitives/scope/nest-rows';

import type { OverviewRegionRow } from './overview-types';
import { RegionRosterRow } from './RegionRosterRow';
import { ROSTER_CELL } from './roster-cell';
import { gridTemplateFor, ROSTER_COLUMNS } from './roster-columns';

export interface RegionRosterTreeProps {
  /** Flat, parent before child, exactly as the server sent them. */
  rows: OverviewRegionRow[];
}

export function RegionRosterTree({ rows }: RegionRosterTreeProps) {
  const forest = nestRows(rows);
  return (
    <div role="table" aria-label="Regions">
      <div
        role="row"
        style={{
          display: 'grid',
          gridTemplateColumns: gridTemplateFor(ROSTER_COLUMNS),
          alignItems: 'center',
          minHeight: 32,
        }}
      >
        {ROSTER_COLUMNS.map((column) => (
          <span
            key={column.id}
            role="columnheader"
            style={{
              ...ROSTER_CELL,
              justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              color: 'var(--nb-subtle)',
            }}
          >
            {column.label}
          </span>
        ))}
      </div>
      {forest.map((node) => (
        <RegionRosterRow key={node.row.id} node={node} indent={0} />
      ))}
    </div>
  );
}
